<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="trends-modal-backdrop" @click="closeIfBackdrop($event)">
      <div class="trends-modal" role="dialog">
        <!-- HEADER -->
        <header class="trends-modal-header">
          <h2 class="trends-modal-title">{{ selectedSector }} - Análise de Tendências</h2>
          <button class="trends-close-button" @click="$emit('close')">&times;</button>
        </header>
        
        <!-- BODY -->
        <div class="trends-modal-body">
          <div class="modal-content">
            <!-- PAINEL DE VISÃO GERAL -->
            <div class="overview-panel">
              <div class="score-card">
                <span class="score-title">Avaliação Média Atual</span>
                <div class="score">
                  {{ getCurrentRating() }}<span class="out-of">/5</span>
                </div>
              </div>
              
              <div class="trend-summary-card" :class="getTrendDirection()">
                <div class="trend-icon" :class="getTrendDirection()">
                  <i class="fas" :class="getTrendIconClass()"></i>
                </div>
                <div class="trend-info">
                  <span class="trend-label">Tendência</span>
                  <span class="trend-value">{{ getTrendText() }}</span>
                </div>
              </div>
              
              <div class="growth-card">
                <span class="growth-label">Variação Último Ano</span>
                <div class="growth-value" :class="getGrowthClass()">
                  {{ getLastYearGrowth() }}
                </div>
              </div>
            </div>
            
            <!-- GRÁFICOS E ANÁLISES -->
            <div class="analysis-grid">
              <!-- Painel de Tendência Histórica -->
              <div class="panel trend-history-panel full-width">
                <h3 class="panel-title">Tendência Histórica</h3>
                <div class="chart-container">
                  <canvas id="trendChart" width="800" height="250"></canvas>
                </div>
                <div class="insight-box">
                  <i class="fas fa-lightbulb insight-icon"></i>
                  <p class="insight-text">{{ getHistoricalInsight() }}</p>
                </div>
              </div>
              
              <!-- Painel de Crescimento Anual -->
              <div class="panel growth-panel full-width">
                <h3 class="panel-title">Crescimento Anual</h3>
                <div class="growth-bars">
                  <div v-for="(item, index) in sectorData.yearlyGrowth" :key="index" class="growth-bar-container">
                    <div class="growth-period">{{ item.period }}</div>
                    <div class="growth-bar-wrapper">
                      <div 
                        class="growth-bar" 
                        :class="getGrowthBarClass(item.growth)"
                        :style="{ width: getGrowthBarWidth(item.growth) }"
                      ></div>
                      <div class="growth-value-container">
                        <span class="growth-value">{{ item.growth === 'N/A' ? 'N/A' : item.growth + '%' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- RECOMENDAÇÕES PARA GESTORES -->
            <div class="recommendations-panel">
              <h3 class="panel-title">Insights</h3>
              <div class="recommendations-grid">
                <div class="recommendation-card">
                  <div class="recommendation-icon">
                    <i class="fas fa-chart-line"></i>
                  </div>
                  <h4>Monitoramento</h4>
                  <p>{{ getMonitoringRecommendation() }}</p>
                </div>
                <div class="recommendation-card">
                  <div class="recommendation-icon">
                    <i class="fas fa-calendar-alt"></i>
                  </div>
                  <h4>Sazonalidade</h4>
                  <p>{{ getSeasonalRecommendation() }}</p>
                </div>
                <div class="recommendation-card">
                  <div class="recommendation-icon">
                    <i class="fas fa-bullseye"></i>
                  </div>
                  <h4>Intervenção</h4>
                  <p>{{ getInterventionRecommendation() }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- FOOTER -->
        <footer class="trends-modal-footer">
          <button class="trends-close-btn" @click="$emit('close')">Fechar</button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { Chart, registerables } from 'chart.js'
Chart.register(...registerables)

export default {
  props: {
    isOpen: Boolean,
    selectedSector: String,
    sectorData: Object
  },
  data() {
    return {
      trendChart: null,
      seasonalityChart: null
    }
  },
  watch: {
    isOpen(val) {
      if (val) {
        document.body.style.overflow = 'hidden'
        this.$nextTick(() => {
          this.createTrendChart()
          this.createSeasonalityChart()
        })
      } else {
        document.body.style.overflow = ''
        this.destroyCharts()
      }
    }
  },
  mounted() {
    if (this.isOpen && this.sectorData) {
      document.body.style.overflow = 'hidden'
      this.$nextTick(() => {
        this.createTrendChart()
        this.createSeasonalityChart()
      })
    }
  },
  beforeDestroy() {
    document.body.style.overflow = ''
    this.destroyCharts()
  },
  methods: {
    closeIfBackdrop(event) {
      if (event.target.className === 'trends-modal-backdrop') {
        this.$emit('close')
      }
    },
    destroyCharts() {
      if (this.trendChart) {
        this.trendChart.destroy()
        this.trendChart = null
      }
      if (this.seasonalityChart) {
        this.seasonalityChart.destroy()
        this.seasonalityChart = null
      }
    },
    createTrendChart() {
      const canvas = document.getElementById('trendChart')
      if (!canvas) return
      
      // Destruir gráfico anterior se existir
      if (this.trendChart) {
        this.trendChart.destroy()
        this.trendChart = null
      }
      
      const ctx = canvas.getContext('2d')
      
      // Verificar se há dados para exibir
      if (!this.sectorData || !this.sectorData.yearlyTrend || this.sectorData.yearlyTrend.length === 0) {
        // Criar um gráfico com mensagem de "Nenhum dado disponível"
        this.trendChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: ['Nenhum dado disponível'],
            datasets: [{
              data: [0],
              backgroundColor: '#f1f5f9'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                enabled: false
              }
            },
            scales: {
              y: {
                display: false
              },
              x: {
                ticks: {
                  color: '#64748b',
                  font: {
                    weight: 'bold'
                  }
                }
              }
            }
          }
        })
        return
      }
      
      // Preparar dados
      const labels = this.sectorData.yearlyTrend.map(item => item.year)
      const data = this.sectorData.yearlyTrend.map(item => item.rating)
      
      // Criar gráfico
      this.trendChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [{
            label: 'Avaliação Média',
            data: data,
            backgroundColor: 'rgba(99, 205, 196, 0.2)',
            borderColor: '#63CDC4',
            borderWidth: 3,
            pointBackgroundColor: '#324DDD',
            pointBorderColor: '#fff',
            pointRadius: 6,
            pointHoverRadius: 8,
            tension: 0.3,
            fill: true
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              left: 10,
              right: 10,
              top: 10,
              bottom: 10
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              min: 0,
              max: 5,
              ticks: {
                stepSize: 1
              }
            }
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return `${context.parsed.y.toFixed(1)} / 5.0`;
                }
              }
            }
          },
          hover: {
            mode: 'nearest',
            intersect: true
          }
        }
      })
    },
    
    createSeasonalityChart() {
      const canvas = document.getElementById('seasonalityChart')
      if (!canvas) return
      
      // Destruir gráfico anterior se existir
      if (this.seasonalityChart) {
        this.seasonalityChart.destroy()
        this.seasonalityChart = null
      }
      
      const ctx = canvas.getContext('2d')
      
      // Verificar se há dados para exibir
      if (!this.sectorData || !this.sectorData.monthlySeasonal || this.sectorData.monthlySeasonal.length === 0) {
        // Criar um gráfico com mensagem de "Nenhum dado disponível"
        this.seasonalityChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: ['Nenhum dado disponível'],
            datasets: [{
              data: [0],
              backgroundColor: '#f1f5f9'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                enabled: false
              }
            },
            scales: {
              y: {
                display: false
              },
              x: {
                ticks: {
                  color: '#64748b',
                  font: {
                    weight: 'bold'
                  }
                }
              }
            }
          }
        })
        return
      }
      
      // Preparar dados
      const labels = this.sectorData.monthlySeasonal.map(item => item.month)
      const data = this.sectorData.monthlySeasonal.map(item => item.rating)
      
      // Criar gráfico
      this.seasonalityChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [{
            label: 'Avaliação Média',
            data: data,
            backgroundColor: context => {
              const value = context.raw
              if (value >= 4) return '#0B8C0B'
              if (value >= 3) return '#63CDC4'
              if (value >= 2) return '#FFC700'
              return '#F25362'
            },
            borderRadius: 6,
            barThickness: 20,
            maxBarThickness: 25
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              left: 10,
              right: 10,
              top: 10,
              bottom: 10
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              min: 0,
              max: 5,
              ticks: {
                stepSize: 1
              }
            },
            x: {
              ticks: {
                autoSkip: true,
                maxRotation: 45,
                minRotation: 45
              }
            }
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return `${context.parsed.y.toFixed(1)} / 5.0`;
                }
              }
            }
          },
          hover: {
            mode: 'nearest',
            intersect: true
          }
        }
      })
    },
    
    getCurrentRating() {
      if (!this.sectorData || !this.sectorData.yearlyTrend || !this.sectorData.yearlyTrend.length) {
        return '0.0';
      }
      
      const currentYearIndex = this.sectorData.yearlyTrend.length - 1;
      return this.sectorData.yearlyTrend[currentYearIndex].rating.toFixed(1);
    },
    
    getTrendDirection() {
      if (!this.sectorData || !this.sectorData.yearlyTrend || this.sectorData.yearlyTrend.length < 2) {
        return 'neutral';
      }
      
      const lastIndex = this.sectorData.yearlyTrend.length - 1;
      const currentRating = this.sectorData.yearlyTrend[lastIndex].rating;
      const previousRating = this.sectorData.yearlyTrend[lastIndex - 1].rating;
      
      if (currentRating > previousRating) return 'positive';
      if (currentRating < previousRating) return 'negative';
      return 'neutral';
    },
    
    getTrendIconClass() {
      const direction = this.getTrendDirection();
      if (direction === 'positive') return 'fa-arrow-up';
      if (direction === 'negative') return 'fa-arrow-down';
      return 'fa-arrow-right';
    },
    
    getTrendText() {
      const direction = this.getTrendDirection();
      if (direction === 'positive') return 'Em alta';
      if (direction === 'negative') return 'Em queda';
      return 'Estável';
    },
    
    getLastYearGrowth() {
      if (!this.sectorData || !this.sectorData.yearlyGrowth || !this.sectorData.yearlyGrowth.length) {
        return 'N/A';
      }
      
      const lastGrowth = this.sectorData.yearlyGrowth[this.sectorData.yearlyGrowth.length - 1];
      return lastGrowth.growth === 'N/A' ? 'N/A' : `${lastGrowth.growth}%`;
    },
    
    getGrowthClass() {
      const growth = this.getLastYearGrowth();
      if (growth === 'N/A') return 'neutral';
      
      const value = parseFloat(growth);
      if (isNaN(value)) return 'neutral';
      
      if (value > 0) return 'positive';
      if (value < 0) return 'negative';
      return 'neutral';
    },
    
    getHistoricalInsight() {
      if (!this.sectorData || !this.sectorData.yearlyTrend || !this.sectorData.yearlyTrend.length) {
        return `Não há dados históricos suficientes para analisar o setor de ${this.selectedSector || 'selecionado'}.`;
      }
      
      const trend = this.getTrendDirection();
      const currentRating = this.getCurrentRating();
      
      if (trend === 'positive') {
        return `O setor de ${this.selectedSector} tem mostrado uma tendência de melhoria consistente, alcançando a pontuação atual de ${currentRating}/5. Este crescimento indica uma percepção positiva crescente dos usuários.`;
      } else if (trend === 'negative') {
        return `O setor de ${this.selectedSector} tem apresentado uma tendência de queda, com pontuação atual de ${currentRating}/5. Recomenda-se analisar as causas e implementar medidas corretivas.`;
      } else {
        return `O setor de ${this.selectedSector} mantém uma pontuação estável de ${currentRating}/5, indicando consistência na percepção dos usuários ao longo do tempo.`;
      }
    },
    
    getPeakMonth() {
      if (!this.sectorData || !this.sectorData.monthlySeasonal || !this.sectorData.monthlySeasonal.length) {
        return 'N/A';
      }
      
      let peak = this.sectorData.monthlySeasonal[0];
      this.sectorData.monthlySeasonal.forEach(item => {
        if (item.rating > peak.rating) peak = item;
      });
      
      return peak.month;
    },
    
    getLowMonth() {
      if (!this.sectorData || !this.sectorData.monthlySeasonal || !this.sectorData.monthlySeasonal.length) {
        return 'N/A';
      }
      
      let low = this.sectorData.monthlySeasonal[0];
      this.sectorData.monthlySeasonal.forEach(item => {
        if (item.rating < low.rating) low = item;
      });
      
      return low.month;
    },
    
    getGrowthBarClass(growth) {
      if (growth === 'N/A') return 'neutral';
      
      const value = parseFloat(growth);
      if (isNaN(value)) return 'neutral';
      
      if (value > 0) return 'positive';
      if (value < 0) return 'negative';
      return 'neutral';
    },
    
    getGrowthBarWidth(growth) {
      if (growth === 'N/A') return '5%';
      
      const value = parseFloat(growth);
      if (isNaN(value)) return '5%';
      
      const absValue = Math.abs(value);
      
      // Limitando o tamanho máximo da barra para 90%
      const maxWidth = 90;
      const widthPercentage = Math.min(maxWidth, Math.max(10, absValue * 3));
      
      return `${widthPercentage}%`;
    },
    
    getMonitoringRecommendation() {
      if (!this.sectorData || !this.selectedSector) {
        return 'Estabeleça um sistema de monitoramento contínuo para acompanhar as avaliações do setor.';
      }
      
      const trend = this.getTrendDirection();
      
      if (trend === 'positive') {
        return `Continue monitorando os fatores que contribuem para a alta nas avaliações do setor de ${this.selectedSector}, especialmente nos períodos de pico.`;
      } else if (trend === 'negative') {
        return `Intensifique o monitoramento das avaliações negativas em ${this.selectedSector} para identificar rapidamente problemas recorrentes e padrões de insatisfação.`;
      } else {
        return `Mantenha o monitoramento regular das avaliações do setor de ${this.selectedSector} para garantir a manutenção da qualidade.`;
      }
    },
    
    getSeasonalRecommendation() {
      if (!this.sectorData) {
        return 'Desenvolva estratégias sazonais para diferentes períodos do ano.';
      }
      
      const peak = this.getPeakMonth();
      const low = this.getLowMonth();
      
      if (peak !== 'N/A' && low !== 'N/A' && peak !== low) {
        return `Planeje campanhas promocionais para ${low} quando as avaliações tendem a cair, e capitalize a boa visibilidade em ${peak}, quando o setor atinge seu pico de avaliações.`;
      }
      
      return `Desenvolva estratégias sazonais para mitigar quedas de avaliação em períodos críticos e potencializar períodos favoráveis.`;
    },
    
    getInterventionRecommendation() {
      if (!this.sectorData) {
        return 'Implemente um programa de coleta de feedback mais detalhado para compreender melhor as necessidades dos usuários.';
      }
      
      const growth = this.getLastYearGrowth();
      
      if (growth === 'N/A') {
        return `Implemente um programa de coleta de feedback mais detalhado para compreender melhor as necessidades dos usuários neste setor.`;
      }
      
      const value = parseFloat(growth);
      if (isNaN(value)) {
        return `Estabeleça métricas claras para medir o desempenho do setor e definir metas de crescimento objetivas.`;
      }
      
      if (value < -5) {
        return `Intervenção urgente recomendada. Organize reuniões com estabelecimentos do setor para discutir estratégias de melhoria na experiência do cliente.`;
      } else if (value < 0) {
        return `Considere realizar workshops com os principais estabelecimentos do setor para compartilhar boas práticas e reverter a tendência negativa.`;
      } else if (value > 10) {
        return `Documente as práticas bem-sucedidas deste setor para replicar em outras áreas com desempenho inferior.`;
      } else {
        return `Mantenha contato regular com estabelecimentos do setor, oferecendo incentivos para melhoria contínua na qualidade.`;
      }
    }
  }
}
</script>

<style scoped>
/* Estilos do Modal */
.trends-modal-backdrop {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.trends-modal {
  background: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  width: 80vw;
  max-width: 1400px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  z-index: 10000;
  overflow: hidden;
}

.trends-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
}

.trends-modal-title {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.trends-close-button {
  background: none;
  border: none;
  font-size: 28px;
  color: #64748b;
  cursor: pointer;
  transition: color 0.2s;
}

.trends-close-button:hover {
  color: #ef4444;
}

.trends-modal-body {
  padding: 0;
  overflow-y: auto;
  max-height: calc(90vh - 140px);
}

.trends-modal-footer {
  padding: 15px 30px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  background: #f8fafc;
}

.trends-close-btn {
  background: #324DDD;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.trends-close-btn:hover {
  background: #2438b3;
}

/* Animações do Modal */
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

/* Conteúdo existente */
.modal-content {
  padding: 30px;
}

.chart-container {
  position: relative;
  height: 250px;
  width: 100%;
}

/* PAINEL DE VISÃO GERAL */
.overview-panel {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.score-card, .trend-summary-card, .growth-card {
  flex: 1;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.score-card {
  background: linear-gradient(135deg, #324DDD, #9747FF);
  color: white;
}

.score-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  opacity: 0.9;
}

.score {
  font-size: 42px;
  font-weight: 700;
  line-height: 1;
}

.out-of {
  font-size: 20px;
  opacity: 0.8;
}

.trend-summary-card {
  background: #fff;
  border-top: 6px solid transparent;
}

.trend-summary-card.positive {
  border-color: #0B8C0B;
}

.trend-summary-card.negative {
  border-color: #F25362;
}

.trend-summary-card.neutral {
  border-color: #64748b;
}

.trend-icon {
  font-size: 32px;
  margin-bottom: 10px;
}

.trend-icon.positive {
  color: #0B8C0B;
}

.trend-icon.negative {
  color: #F25362;
}

.trend-icon.neutral {
  color: #64748b;
}

.trend-label {
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  display: block;
  margin-bottom: 5px;
}

.trend-value {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
}

.growth-card {
  background: #f8fafc;
  border-left: 1px solid #e2e8f0;
}

.growth-label {
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 10px;
}

.growth-value {
  font-size: 28px;
  font-weight: 700;
  width: 100%;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
}

.growth-value.positive {
  color: #0B8C0B;
}

.growth-value.negative {
  color: #F25362;
}

.growth-value.neutral {
  color: #64748b;
}

/* GRÁFICOS E ANÁLISES */
.analysis-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 30px;
}

.panel {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}

.full-width {
  grid-column: 1 / -1;
  margin-bottom: 24px;
}

.panel-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #e2e8f0;
}

.trend-history-panel {
  min-height: 350px;
}

/* Caixa de Insights */
.insight-box {
  display: flex;
  align-items: flex-start;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
}

.insight-icon {
  color: #324DDD;
  font-size: 18px;
  margin-right: 12px;
  margin-top: 2px;
}

.insight-text {
  font-size: 14px;
  color: #475569;
  margin: 0;
  line-height: 1.5;
}

/* Barras de Crescimento */
.growth-bars {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.growth-bar-container {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.growth-period {
  width: 80px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  flex-shrink: 0;
}

.growth-bar-wrapper {
  flex: 1;
  height: 24px;
  background: #f1f5f9;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.growth-bar {
  height: 100%;
  border-radius: 12px;
  transition: width 0.5s ease;
}

.growth-bar.positive {
  background: #0B8C0B;
}

.growth-bar.negative {
  background: #F25362;
}

.growth-bar.neutral {
  background: #64748b;
}

.growth-value-container {
  position: absolute;
  right: 10px;
  width: auto;
  text-align: right;
  z-index: 2;
}

.growth-value {
  font-size: 14px;
  font-weight: 700;
  color: white;
  text-shadow: 0px 0px 3px rgba(0, 0, 0, 0.7);
}

/* PAINEL DE RECOMENDAÇÕES */
.recommendations-panel {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-top: 24px;
}

.recommendations-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 20px;
}

.recommendation-card {
  background: #f8fafc;
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.recommendation-icon {
  width: 50px;
  height: 50px;
  background: #324DDD;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  margin-bottom: 16px;
}

.recommendation-card h4 {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
}

.recommendation-card p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

/* RESPONSIVIDADE */
@media (max-width: 1024px) {
  .analysis-grid {
    grid-template-columns: 1fr;
  }
  
  .recommendations-grid {
    grid-template-columns: 1fr;
  }
  
  .overview-panel {
    flex-direction: column;
  }
  
  .score-card, .trend-summary-card, .growth-card {
    width: 100%;
  }
  
  .growth-period {
    width: 60px;
  }
  
  .full-width {
    grid-column: auto;
  }
}
</style> 